import { useMemo, useState, useCallback } from 'react'
import { downloadVideo } from '@/lib/download'
import { track } from '@/lib/track'
import { Link } from 'react-router-dom'
import { useMutation, useAction } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Doc, Id } from '../../../convex/_generated/dataModel'
import { VideoCard, VideoItem } from './VideoCard'
import { VideoPlayer } from './VideoPlayer'
import { PlyrPlayer } from './PlyrPlayer'
import { Switch } from '@/components/ui/switch'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { PenLine, Loader2, Trash2, Check, Download, Code2, Copy, ArrowRight, Share2, Monitor } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cdnUrl, siteUrl } from '@/lib/config/site'

type WorkflowWithCover = Doc<'workflows'> & { coverImageUrl?: string | null; clipCount?: number }

interface ProjectGridProps {
  workflows: WorkflowWithCover[]
  videos: VideoItem[]
  isLoading?: boolean
  onLegacyVideoCleared?: () => void
}

interface WorkflowProjectCardProps {
  workflow: WorkflowWithCover
  onDelete?: (id: string) => void
}

function WorkflowProjectCard({ workflow }: WorkflowProjectCardProps) {
  const hasCoverImage = !!workflow.coverImageUrl
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [copied, setCopied] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [embedOpen, setEmbedOpen] = useState(false)
  const [embedCopied, setEmbedCopied] = useState(false)
  const [embedAutoplay, setEmbedAutoplay] = useState(false)
  const [embedMuted, setEmbedMuted] = useState(false)
  const [embedLoop, setEmbedLoop] = useState(false)
  const [embedControls, setEmbedControls] = useState(true)
  const deleteWorkflow = useMutation(api.workflows.deleteWorkflow)

  const isCompleted = workflow.finalVideoId && workflow.finalVideoUrl
  const urlCode = workflow.shortcode ?? workflow._id
  const shareUrl = isCompleted
    ? siteUrl(`/share/${urlCode}`)
    : null

  const handleCopyLink = async () => {
    if (!shareUrl) return
    await navigator.clipboard.writeText(shareUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    track('video.share_link_copied', { videoId: urlCode })
    track('project.shared', { workflowId: workflow._id, channel: 'link_copy' })
  }

  const embedCode = useMemo(() => {
    if (!isCompleted) return ''
    const params = new URLSearchParams()
    if (embedAutoplay) params.set('autoplay', '1')
    if (embedMuted) params.set('muted', '1')
    if (embedLoop) params.set('loop', '1')
    if (!embedControls) params.set('controls', '0')
    const qs = params.toString()
    const embedUrl = siteUrl(`/embed/${urlCode}${qs ? `?${qs}` : ''}`)
    return `<iframe src="${embedUrl}" width="640" height="360" frameborder="0" allowfullscreen></iframe>`
  }, [isCompleted, urlCode, embedAutoplay, embedMuted, embedLoop, embedControls])

  const handleCopyEmbed = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(embedCode)
      setEmbedCopied(true)
      setTimeout(() => setEmbedCopied(false), 2000)
    } catch {
      // fallback
    }
  }, [embedCode])

  const handleDownload = async () => {
    if (!workflow.finalVideoUrl) return
    await downloadVideo(workflow.finalVideoUrl, `${workflow.title || 'video'}.mp4`)
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setConfirmingDelete(true)
  }

  const handleConfirmDelete = async () => {
    setIsDeleting(true)
    try {
      await deleteWorkflow({ id: workflow._id })
    } catch (error) {
      console.error('Failed to delete project:', error)
      setIsDeleting(false)
      setConfirmingDelete(false)
    }
  }

  const handleCancelDelete = () => {
    setConfirmingDelete(false)
  }

  return (
    <div className="group bg-card rounded-lg border border-border overflow-hidden shadow-xs hover:shadow-sm hover:border-border/80 transition-all">
      {/* Thumbnail */}
      <div className="h-[180px] bg-linear-to-br from-neutral-100 to-neutral-50 relative flex items-center justify-center overflow-hidden">
        <Link to={`/video/${urlCode}`} className="block w-full h-full flex items-center justify-center">
          {hasCoverImage ? (
            <img
              src={workflow.coverImageUrl!}
              alt={workflow.title || 'Video thumbnail'}
              className={cn("w-full h-full", workflow.settings?.aspectRatio === '9:16' || workflow.settings?.aspectRatio === '1:1' ? "object-contain" : "object-cover")}
            />
          ) : (
            <img src={cdnUrl('/static/pencil.png')} alt="Draft" className="w-full h-full object-cover" />
          )}
        </Link>

        {/* Action icons (top right) */}
        {!confirmingDelete && (
          <div className="absolute top-2 right-2 flex flex-col gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-all z-10">
            <button
              onClick={handleDeleteClick}
              className="p-1.5 rounded-md bg-white/90 text-neutral-500 hover:bg-red-50 hover:text-red-600 transition-all shadow-sm"
            >
              <Trash2 size={16} />
            </button>
            {isCompleted && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDownload() }}
                className="p-1.5 rounded-md bg-white/90 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition-all shadow-sm"
              >
                <Download size={16} />
              </button>
            )}
          </div>
        )}

        {/* Delete confirmation overlay */}
        {confirmingDelete && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20 animate-in fade-in duration-100">
            <div className="flex flex-col items-center gap-3">
              <span className="text-sm text-white font-medium">Delete this video?</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancelDelete}
                  disabled={isDeleting}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-white/20 hover:bg-white/30 rounded-md transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors disabled:opacity-50 flex items-center gap-1.5"
                >
                  {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Title row */}
      <div className="px-3 py-2.5 flex items-center justify-between gap-2">
        <h3 className="font-medium text-neutral-900 text-sm truncate">
          {workflow.title || 'Untitled Video'}
        </h3>
        <div className="flex items-center gap-1.5 shrink-0">
          {(workflow.source === 'cli' || workflow.source === 'desktop') && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-violet-50 text-violet-600">
              <Monitor size={10} />
              Desktop
            </span>
          )}
          <span className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium',
            isCompleted
              ? 'bg-sky-50 text-sky-600'
              : 'bg-muted text-muted-foreground'
          )}>
            {isCompleted ? 'Ready' : 'Draft'}
          </span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="border-t border-border">
        {isCompleted ? (
          <div className="grid grid-cols-2 divide-x divide-border">
            <button
              onClick={() => setShareOpen(true)}
              className="flex items-center justify-center gap-1.5 py-2 text-[12px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <Share2 size={14} />
              Share
            </button>
            <button
              onClick={() => setEmbedOpen(true)}
              className="flex items-center justify-center gap-1.5 py-2 text-[12px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <Code2 size={14} />
              Embed
            </button>
          </div>
        ) : (
          <Link
            to={`/video/${urlCode}`}
            className="flex items-center justify-center gap-1.5 py-2 text-[12px] font-medium text-neutral-600 hover:bg-neutral-50 transition-colors"
          >
            Complete this video
            <ArrowRight size={14} />
          </Link>
        )}
      </div>

      {/* Share modal */}
      {isCompleted && (
        <Dialog open={shareOpen} onOpenChange={setShareOpen}>
          <DialogContent className="sm:max-w-lg bg-white border-neutral-200 text-neutral-900">
            <DialogHeader>
              <DialogTitle>Share Video</DialogTitle>
              <DialogDescription className="text-neutral-500">
                Share a link to this video.
              </DialogDescription>
            </DialogHeader>

            {/* Video preview */}
            <div className="modal-player rounded-lg overflow-hidden bg-black aspect-video">
              <PlyrPlayer
                src={workflow.finalVideoUrl!}
                poster={workflow.coverImageUrl || undefined}
              />
              <style>{`
                .modal-player .plyr { border-radius: 0 !important; }
                .modal-player .plyr-wrapper {
                  --plyr-color-main: rgba(255,255,255,0.8);
                  --plyr-video-control-color: rgba(255,255,255,0.7);
                  --plyr-video-control-background-hover: rgba(255,255,255,0.15);
                }
                .modal-player .plyr-wrapper .plyr__control--overlaid {
                  background: rgba(255,255,255,0.15) !important;
                  backdrop-filter: blur(8px);
                  padding: 20px !important;
                }
                .modal-player .plyr-wrapper .plyr__control--overlaid svg {
                  width: 22px !important;
                  height: 22px !important;
                }
                .modal-player .plyr-wrapper .plyr__control--overlaid:hover {
                  background: rgba(255,255,255,0.25) !important;
                }
                .modal-player .plyr-wrapper .plyr--full-ui input[type=range] {
                  color: rgba(255,255,255,0.8) !important;
                }
              `}</style>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg p-3 text-sm font-mono break-all leading-relaxed bg-neutral-100 text-neutral-600 ring-1 ring-black/5">
                {shareUrl}
              </div>
              <button
                onClick={handleCopyLink}
                className={cn(
                  'w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors',
                  copied
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-neutral-900 text-white hover:bg-neutral-800'
                )}
              >
                {copied ? (
                  <>
                    <Check size={14} />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy size={14} />
                    Copy Link
                  </>
                )}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Embed modal */}
      {isCompleted && (
        <Dialog open={embedOpen} onOpenChange={setEmbedOpen}>
          <DialogContent className="sm:max-w-lg bg-white border-neutral-200 text-neutral-900">
            <DialogHeader>
              <DialogTitle>Embed Video</DialogTitle>
              <DialogDescription className="text-neutral-500">
                Embed this video on your website.
              </DialogDescription>
            </DialogHeader>

            {/* Video preview */}
            <div className="modal-player rounded-lg overflow-hidden bg-black aspect-video">
              <PlyrPlayer
                src={workflow.finalVideoUrl!}
                poster={workflow.coverImageUrl || undefined}
              />
            </div>

            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm text-neutral-700">Autoplay</label>
                  <Switch
                    checked={embedAutoplay}
                    onCheckedChange={(checked) => {
                      setEmbedAutoplay(checked)
                      if (checked && !embedMuted) setEmbedMuted(true)
                    }}
                    className="data-[state=checked]:bg-primary"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm text-neutral-700">Muted</label>
                  <Switch
                    checked={embedMuted}
                    onCheckedChange={setEmbedMuted}
                    disabled={embedAutoplay}
                    className="data-[state=checked]:bg-primary"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm text-neutral-700">Loop</label>
                  <Switch
                    checked={embedLoop}
                    onCheckedChange={setEmbedLoop}
                    className="data-[state=checked]:bg-primary"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm text-neutral-700">Show controls</label>
                  <Switch
                    checked={embedControls}
                    onCheckedChange={setEmbedControls}
                    className="data-[state=checked]:bg-primary"
                  />
                </div>
                {embedAutoplay && (
                  <p className="text-xs text-neutral-400">
                    Browsers require muted audio for autoplay to work.
                  </p>
                )}
              </div>
              <div className="rounded-lg p-3 text-xs font-mono break-all leading-relaxed bg-neutral-100 text-neutral-600 ring-1 ring-black/5">
                {embedCode}
              </div>
              <button
                onClick={handleCopyEmbed}
                className={cn(
                  'w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors',
                  embedCopied
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-neutral-900 text-white hover:bg-neutral-800'
                )}
              >
                {embedCopied ? (
                  <>
                    <Check size={14} />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy size={14} />
                    Copy Embed Code
                  </>
                )}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

export function ProjectGrid({
  workflows,
  videos,
  isLoading = false,
  onLegacyVideoCleared,
}: ProjectGridProps) {
  const [deletingVideos, setDeletingVideos] = useState<Set<string>>(new Set())
  const [playingVideo, setPlayingVideo] = useState<VideoItem | null>(null)
  const deleteVideo = useMutation(api.videos.deleteVideo)
  const clearLegacyVideo = useAction(api.legacyVideos.clearLegacyVideo)

  // Combine and sort by updatedAt/createdAt descending
  const sortedItems = useMemo(() => {
    const workflowItems = workflows.map(w => ({
      type: 'workflow' as const,
      item: w,
      sortTime: w.updatedAt
    }))
    const videoItems = videos.map(v => ({
      type: 'video' as const,
      item: v,
      sortTime: v.createdAt
    }))
    return [...workflowItems, ...videoItems].sort((a, b) => b.sortTime - a.sortTime)
  }, [workflows, videos])

  const handleDeleteVideo = async (videoId: string, isLegacy?: boolean) => {
    setDeletingVideos((prev) => new Set([...prev, videoId]))
    try {
      if (isLegacy) {
        await clearLegacyVideo({ legacyVideoId: videoId })
        onLegacyVideoCleared?.()
      } else {
        await deleteVideo({ id: videoId as Id<'videos'> })
      }
    } catch (error) {
      console.error('Failed to delete video:', error)
    } finally {
      setDeletingVideos((prev) => {
        const newSet = new Set(prev)
        newSet.delete(videoId)
        return newSet
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-500" />
      </div>
    )
  }

  if (sortedItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
          <PenLine className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No videos yet</h3>
        <p className="text-muted-foreground max-w-sm">
          Create your first video to get started
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="@container max-w-[1100px] mx-auto"><div className="grid grid-cols-1 @[540px]:grid-cols-2 @[900px]:grid-cols-3 gap-4">
        {sortedItems.map((entry) => {
          if (entry.type === 'workflow') {
            return (
              <WorkflowProjectCard
                key={entry.item._id}
                workflow={entry.item as WorkflowWithCover}
                onDelete={() => {}}
              />
            )
          } else {
            const video = entry.item as VideoItem
            const videoId = 'isLegacy' in video && video.isLegacy ? video.id : video._id
            return (
              <VideoCard
                key={videoId}
                video={video}
                onDelete={handleDeleteVideo}
                onPlay={setPlayingVideo}
                isDeleting={deletingVideos.has(videoId)}
              />
            )
          }
        })}
      </div></div>

      {/* Video Player Modal */}
      {playingVideo && (
        <VideoPlayer
          video={playingVideo}
          onClose={() => setPlayingVideo(null)}
        />
      )}
    </>
  )
}
